// Stencil
import {
  Component,
  Host,
  h,
  Prop,
  Listen,
  // Watch,
  Element,
  Event,
  EventEmitter
} from "@stencil/core";

// Custom Imports
import { MercuryBundles } from "@genexus/mercury";
import { ItemData } from "./list-selector-item/list-selector-item";

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "chameleon/scrollbar"
];

@Component({
  tag: "gx-ide-list-selector",
  styleUrl: "list-selector.scss",
  shadow: true
})
export class GxIdeListSelector {
  @Element() el: HTMLGxIdeListSelectorElement;

  /**
   * It determines if the list is ordered, or unordered
   */
  @Prop() readonly ordered: boolean = false;

  /**
   * Makes the list readonly
   */
  @Prop() readonly readonly: boolean = false;

  /**
   * It determines the type of list: single-selection or multi-selection
   */
  @Prop() readonly type: "single-selection" | "multi-selection" =
    "single-selection";

  /**
   * Makes the list required
   */
  @Prop() readonly required: boolean = false;

  /**
   * The presence of this property will force all the items to not-wrap, and have ellipsis
   */
  @Prop() readonly ellipsis: boolean = false;

  /**
   * The value of the initial (or initials) checked list-item/s
   */
  @Prop({ mutable: true }) value: string | string[];

  // @Watch("value")
  // valueHandler(newValue: string) {
  //   const foundItem = this.getItemByValue(newValue);
  //   if (foundItem) {
  //     const input = foundItem.querySelector("input");
  //     input.checked = true;
  //   }
  // }

  /**
   * The name of this list. This is a required attribute for the list to work.
   */
  @Prop() readonly listName!: string;

  /**
   * Disables the items border
   */
  @Prop() readonly noItemsBorder: boolean = false;

  /**
   * Disables the list border
   */
  @Prop({ reflect: true }) readonly noListBorder: boolean = false;

  /**
   * Emits the actual selection
   */
  @Event() selectionChanged: EventEmitter<CheckedItemsInfo>;

  componentWillLoad() {
    this.evaluateChecked();
    this.evaluateInitialValue();
  }

  @Listen("itemSelectionChanged", { capture: true })
  itemSelectionChangedHandler(event: CustomEvent<ItemData>) {
    event.stopPropagation();
    const selectedItem = event.detail;
    if (this.type === "single-selection") {
      this.clearPrevSelected(selectedItem);
    }
    const checkedItemsInfo: CheckedItemsInfo = [];
    const checkedItems: HTMLGxIdeListSelectorItemElement[] = this.getChecked();
    const newValue: string | string[] = [];
    checkedItems.forEach(item => {
      const value = item.itemValue;
      newValue.push(value);
      checkedItemsInfo.push({
        itemId: item.itemId,
        itemValue: value,
        itemIcon: item.icon,
        itemChecked: true
      });
    });
    /* update this.value*/
    if (this.type === "single-selection" && newValue.length > 0) {
      this.value = newValue[0] || undefined;
    } else {
      /* multi-selection*/
      this.value = newValue;
    }
    /* emit selectionChanged*/
    this.selectionChanged.emit(checkedItemsInfo);
  }

  /**
   * It evaluates the checked items, and checks or unchecks if needed
   */
  private evaluateChecked = () => {
    const allListItems = Array.from(
      this.el.querySelectorAll("gx-ide-list-selector-item")
    );
    const checkedItems = allListItems.filter(item => item.itemChecked);
    /**
     * INDEX
     * 1.required and no one checked
     * 2.single-selection, and more than one checked
     */

    /* 1.required and no one checked*/
    if (this.required && checkedItems.length === 0) {
      allListItems[0].itemChecked = true;
    } else if (this.type === "single-selection" && checkedItems.length > 1) {
      /* 2.single-selection, and more than one checked*/
      checkedItems.forEach((item, i) => {
        if (i !== 0) {
          item.itemChecked = false;
        }
      });
    }
  };

  /**
   * It evaluates the initial value/s
   */
  private evaluateInitialValue = () => {
    const checkedItems = this.getChecked();
    if (this.type === "single-selection" && checkedItems.length > 0) {
      this.value = checkedItems[0].itemValue;
    } else if (this.type === "multi-selection" && checkedItems.length > 0) {
      const values: string[] = [];
      checkedItems.forEach(checked => {
        values.push(checked.itemValue);
      });
      this.value = values;
    }
  };

  private getChecked = (): HTMLGxIdeListSelectorItemElement[] => {
    const checkedItems: HTMLGxIdeListSelectorItemElement[] = [];
    const allItems = this.el.querySelectorAll("gx-ide-list-selector-item");
    allItems.forEach(item => {
      if (item.itemChecked) {
        checkedItems.push(item);
      }
    });
    return checkedItems;
  };

  private clearPrevSelected = (currentSelected: ItemData) => {
    const allItems = this.el.querySelectorAll("gx-ide-list-selector-item");
    allItems.forEach(item => {
      if (item.itemChecked && item.itemId !== currentSelected.itemId) {
        item.itemChecked = false;
      }
    });
  };

  render() {
    const listClases = "list scrollable";
    return (
      <Host exportparts="input:input">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        {this.ordered ? (
          <ol class={listClases} part="list">
            <slot></slot>
          </ol>
        ) : (
          <ul class={listClases} part="list">
            <slot></slot>
          </ul>
        )}
      </Host>
    );
  }
}

export type CheckedItemsInfo = ItemData[];
